import * as Sentry from "@sentry/react-native";
import * as SecureStore from "expo-secure-store";
import type { LocationObject } from "expo-location";
import { v4 as uuidv4 } from "uuid";

import { getPushNotificationToken } from "./notifications";
import {
  convertAttributionDataToKeyValue,
  getAttributionDataFromStorage,
} from "./campaign";

import { fetchDiagnostics } from "@/screens/Settings/Troubleshooting/Services/diagnosticService";
import { type DeviceTrackingRequest } from "@/schemas/api/tracking";
import {
  convertDiagnosticRunToKeyValue,
  convertExpoLocationToKeyValue,
} from "@/utils/keyvalue";
import { isWeb } from "@/utils/platform";
import { getUsernameFromStorage } from "@/context/auth/AuthContext";
import { LoggingService } from "@/services/internal/logging";
import type { Coordinates } from "@/schemas/location/cordinate";
import {
  DiagnosticCategory,
  type DiagnosticItem,
} from "@/schemas/app/diagnostic";

/**
 * Options for device data logging
 */
export interface DeviceLoggingOptions {
  /** Current device location information */
  location?: LocationObject | null;
  /** Optional update diagnostic items to include in the log */
  updates?: DiagnosticItem[] | null;
  /** Whether to bypass throttling and force logging */
  forceLogging?: boolean;
}

// Key for storing the last log time in SecureStore
export const LAST_LOG_TIME_KEY = "last_device_log_time";
// Key for tracking whether app install has been logged
export const APP_INSTALL_LOGGED_KEY = "app_install_logged";
// Throttle duration in milliseconds (15 minutes)
export const THROTTLE_DURATION = 15 * 60 * 1000;

export async function getDeviceIdentifier(): Promise<{
  deviceId: string;
}> {
  if (isWeb()) {
    return { deviceId: "web" };
  }

  try {
    let deviceId = await SecureStore.getItemAsync("device");
    if (!deviceId) {
      deviceId = uuidv4();
      await SecureStore.setItemAsync("device", deviceId);
    }

    return { deviceId };
  } catch (error) {
    Sentry.captureException(error);
    return { deviceId: uuidv4() };
  }
}

export async function isNewAppInstall(): Promise<boolean> {
  if (isWeb()) {
    return false;
  }

  try {
    const logged = await SecureStore.getItemAsync(APP_INSTALL_LOGGED_KEY);
    return logged === null;
  } catch (error) {
    Sentry.captureException(error);
    return false;
  }
}

export async function markAppInstallLogged(): Promise<void> {
  try {
    await SecureStore.setItemAsync(APP_INSTALL_LOGGED_KEY, "true");
  } catch (error) {
    Sentry.captureException(error);
  }
}

async function shouldAllowLogging(): Promise<boolean> {
  try {
    const lastLogTimeStr = await SecureStore.getItemAsync(LAST_LOG_TIME_KEY);

    // If no previous log time exists, allow logging
    if (!lastLogTimeStr) {
      return true;
    }

    const lastLogTime = parseInt(lastLogTimeStr, 10);
    const currentTime = Date.now();

    // Check if enough time has passed since the last log
    return currentTime - lastLogTime >= THROTTLE_DURATION;
  } catch (error) {
    // If there's an error reading the timestamp, allow logging
    Sentry.captureMessage("Error checking device log throttle time", {
      extra: { error },
    });
    return true;
  }
}

export async function resetLastLogTime(): Promise<void> {
  try {
    await SecureStore.deleteItemAsync(LAST_LOG_TIME_KEY);
  } catch (error) {
    Sentry.captureException(error);
  }
}

/**
 * Updates the last log time to the current time
 */
async function updateLastLogTime(): Promise<void> {
  try {
    const currentTime = Date.now().toString();
    await SecureStore.setItemAsync(LAST_LOG_TIME_KEY, currentTime);
  } catch (error) {
    Sentry.captureException(error);
  }
}

/**
 * Enriches base diagnostics with update information
 * @param diagnostics Base diagnostic data
 * @param updates Optional update information to include
 * @returns Enriched diagnostics with updates if provided
 */
async function enrichDiagnosticsWithUpdates(
  diagnostics: Record<DiagnosticCategory, DiagnosticItem[]>,
  updates: DiagnosticItem[] | null,
): Promise<Record<DiagnosticCategory, DiagnosticItem[]>> {
  if (updates === null) {
    return diagnostics;
  }

  return {
    ...diagnostics,
    [DiagnosticCategory.UPDATE]: updates,
  };
}

export async function logAppInstall(deviceId: string): Promise<void> {
  try {
    const logger = new LoggingService();
    await logger.logAppInstall({ deviceId });
    await markAppInstallLogged();
  } catch (error) {
    Sentry.captureException(error);
  }
}

export async function logDeviceData(
  options: DeviceLoggingOptions = {},
): Promise<void> {
  const {
    location = null,
    updates: updatesData = null,
    forceLogging: force = false,
  } = options;
  if (isWeb()) {
    return;
  }

  // Check if we should allow logging based on the time since the last log
  const shouldLog = await shouldAllowLogging();
  if (!shouldLog && !force) {
    return;
  }

  try {
    // Fetch all required data in parallel for better performance
    const [
      username,
      pushToken,
      { deviceId },
      baseDiagnostics,
      attributionData,
    ] = await Promise.all([
      getUsernameFromStorage(),
      getPushNotificationToken(false),
      getDeviceIdentifier(),
      fetchDiagnostics(null),
      getAttributionDataFromStorage(),
    ]);

    const isLoggedIn = username !== null;

    // Enrich diagnostics with update information using immutable pattern
    const enrichedDiagnostics = await enrichDiagnosticsWithUpdates(
      baseDiagnostics,
      updatesData,
    );

    const items = [
      ...convertDiagnosticRunToKeyValue(enrichedDiagnostics),
      ...convertAttributionDataToKeyValue(attributionData),
    ];

    let coordinates: Coordinates | null = null;

    if (location) {
      const locationValues = convertExpoLocationToKeyValue(location);
      items.push(...locationValues);
      coordinates = {
        latitude: location.coords.latitude,
        longitude: location.coords.longitude,
      };
    }

    const request: DeviceTrackingRequest = {
      deviceId,
      items,
      username,
      pushToken,
      isLoggedIn,
      coordinates,
    };

    const logger = new LoggingService();
    await logger.collectDeviceInformation(request);
    await updateLastLogTime();
  } catch (error) {
    Sentry.captureException(error);
  }
}
